import { forkWorkflowTemplateVersion } from '@commenda/api';
import { AgentMode, IWorkflowTemplateVersion } from '@commenda/types';
import { WorkflowTemplateVersionStatus } from '@prisma/client';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  TWorkflowVersionTarget,
  WorkflowVersionTargetOutcome,
} from './types/types';

/** Copy-on-write owned by the UI: chat only ever writes to a DRAFT. Status is the same predicate the
 * server's write guard uses, so the UI and the guard cannot disagree. */
export function useVersionTargeting(
  params: Readonly<{
    version: IWorkflowTemplateVersion | null;
    onForked: (forkedVersionId: string) => Promise<void> | void;
  }>
) {
  const { version, onForked } = params;
  const [isForking, setIsForking] = useState(false);

  // A ref as well as state: the fork happens before any message exists, so a second click would fork
  // twice before the state update lands.
  const isForkingRef = useRef(false);

  const isEditable = version?.status === WorkflowTemplateVersionStatus.DRAFT;

  const resolveTarget = useCallback(
    async (mode: AgentMode): Promise<TWorkflowVersionTarget> => {
      if (!version) {
        return {
          outcome: WorkflowVersionTargetOutcome.FAILED,
          error: 'No workflow version is loaded yet.',
        };
      }

      // Ask and Plan never write, so they never fork. The read-only tool filter backs this up server-side.
      if (isEditable || mode !== AgentMode.AGENT) {
        return {
          outcome: WorkflowVersionTargetOutcome.EDIT_IN_PLACE,
          targetWorkflowTemplateVersionId: version.id,
        };
      }

      if (isForkingRef.current) {
        return {
          outcome: WorkflowVersionTargetOutcome.FAILED,
          error: 'Still creating a new version to edit. One moment.',
        };
      }

      isForkingRef.current = true;
      setIsForking(true);
      try {
        const result = await forkWorkflowTemplateVersion({
          workflowTemplateId: version.workflowTemplateId,
          sourceWorkflowTemplateVersionId: version.id,
          versionNote: `Copied from ${version.version} to edit with chat.`,
        });

        if (!result.success || !result.data) {
          return {
            outcome: WorkflowVersionTargetOutcome.FAILED,
            error: result.error ?? 'Could not create a new version to edit.',
          };
        }

        const forkedVersionId = result.data.workflowTemplateVersion.id;

        // Bind before navigating: the reverse order can leave the author on an editable draft with an
        // unbound conversation, which silently degrades to the standalone agent page.
        await onForked(forkedVersionId);

        return {
          outcome: WorkflowVersionTargetOutcome.FORKED,
          targetWorkflowTemplateVersionId: forkedVersionId,
        };
      } catch {
        return {
          outcome: WorkflowVersionTargetOutcome.FAILED,
          error: 'Could not reach the server to create a new version.',
        };
      } finally {
        isForkingRef.current = false;
        setIsForking(false);
      }
    },
    [version, isEditable, onForked]
  );

  return useMemo(
    () => ({
      isEditable,
      isForking,
      resolveTarget,
    }),
    [isEditable, isForking, resolveTarget]
  );
}
